import React, { ReactNode, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { breakpoints } from '@gumtree/ui-library/src/base/theme';
import { track } from '@gumtree/shared/src/actions';
import { useFeatureValue } from '@growthbook/growthbook-react';
import {
    PermutiveEventNames,
    trackPermutiveCustomEvent,
} from '@gumtree/shared/src/util/permutive/track-event';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { getSaveAdPath, toggleSavedAdThunk } from '@gumtree/shared/src/reducers/saved-ads';
import type { ShellState, ShellUseDispatch } from '@gumtree/shell/src/reducers/common';
import { PageLayout } from '@gumtree/shared/src/types/srp';
import { useModal } from '@gumtree/shell/src/use-modal';
import { SearchAd } from '@gumtree/shared/src/model/search-ads-model';
import { SellerAd } from '@gumtree/shared/src/model/seller-other-ads-model';
import { getClickListingDetails } from '@gumtree/shared/src/util/listing-details';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import { SellerSkillItem } from '@gumtree/shared/src/model/listing.model';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import { UpdateRequestQuoteFormData } from '@gumtree/shared/src/types/request-quote-form';

import { Favourite, Icon } from '..';

import {
    getListingTileBarkPath,
    handleListingClick,
} from '../listing-tile-v-2023/listing-tile.utils';
import ListingTileThumbnail, { ImageProp } from '../listing-tile-v-2023/listing-tile-thumbnail';
import NoImg from './noImg.svg';

import {
    A,
    TopTagWrapper,
    FavouriteWrapper,
    AContentWrapper,
    ContentWrapper,
    ImageBadge,
    ImageWrapper,
    Wrapper,
    H2,
    SecondWrapper,
    CateName,
    LocationWrapper,
    LocationName,
    ImageScoreWrapper,
} from './listing-tile-service.style';

import TileInfoService from './tile-info-service';
import { qaAttribute } from '../utils/qa-service';
import ScoreReviews from '../score-reviews/score-reviews';

export interface Props {
    style?: React.CSSProperties;
    isLazyLoadImage?: boolean;
    isHighlighted?: boolean;
    children?: ReactNode;
    pageLayout?: PageLayout;
    ad: SearchAd | SellerAd;
    type?: 'listing' | 'profile-listing';
    adSource?: string;
    thirdPartySellerId?: string;
    allSellerSkills?: SellerSkillItem[];
    sellerRating?: {
        average: number;
        total: number;
    };
    updateRequestQuoteFormData?: UpdateRequestQuoteFormData;
    requestId?: string;
}

const ListingTile = ({
    isLazyLoadImage = true,
    ad,
    type = 'listing',
    updateRequestQuoteFormData,
    requestId,
    ...otherProps
}: Props) => {
    const {
        id: adId,
        path,
        imageUrl,
        title,
        showLocation,
        featured = false,
        urgent = false,
        categoryId,
        l1CategoryId,
        categoryName,
        srpContactDetail = {},
        sellerSkills = [],
        adSource = 'GT',
        thirdPartySellerId,
        allSellerSkills,
        sellerRating: { average, total } = {},
    } = ad;

    const inExperiment_GOOGLE_REVIEW_SRP =
        useFeatureValue(GrowthBookFeature.GOOGLE_REVIEW_SRP, 'A') === 'B';

    const isNewUiExperiment = useFeatureValue(GrowthBookFeature.REPURPOSE_URGENT_REVENUE, 'A') === 'B';

    const isNewUiForCategory = useMemo(() => 
        isNewUiExperiment && l1CategoryId === 2554
    ,[isNewUiExperiment, l1CategoryId]);

    const isMobile = useMediaQuery({ maxWidth: breakpoints.medium });
    const image: ImageProp = { src: imageUrl ?? '', alt: title, width: '112px', height: '112px' };
    const { url: seoPath } = getListingTileBarkPath(path);
    const { token, savedAds, userLoggedIn, buyerUrl } = useSelector(
        ({ userSavedAds, userData, baseConfig }: ShellState) => ({
            savedAds: userSavedAds.savedAds,
            token: userSavedAds.token,
            userLoggedIn: !!userData && userData.userLoggedIn,
            buyerUrl: baseConfig.buyerUrl,
            sellerUrl: baseConfig.sellerUrl,
        })
    );

    const { openDialog } = useModal();

    const isShowFavourite = Boolean(token);
    const favourited = Boolean(savedAds[adId]);
    const tileHasImages = true;

    const dispatch = useDispatch() as ShellUseDispatch;

    const toggleFavourite = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();

            if (!favourited) {
                track('WatchlistAddAttempt');
                trackPermutiveCustomEvent(window, PermutiveEventNames.WATCHLIST_ADD_ATTEMPT, {});
            }

            const trackEventPayload = {
                listName: type === 'profile-listing' ? 'Profile' : 'Search Result',
                clickListingDetails: getClickListingDetails(ad),
            };

            if (!userLoggedIn) {
                const redirectUrl = `${buyerUrl}${getSaveAdPath('add', adId, token)}`;
                openDialog(e, LoginModalType.FAVOURITE, true, redirectUrl);
                return;
            }

            dispatch(
                toggleSavedAdThunk({
                    advertId: adId,
                    token: token as string,
                    isAlreadySaved: favourited,
                    trackEventPayload,
                })
            );
        },
        [favourited]
    );

    const handleClick = useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement>) => {
            let newPath = path;
            try {
                const target = e.target as HTMLElement;
                if (
                    target &&
                    typeof target.closest === 'function' &&
                    target.closest('#srp-review-info')
                ) {
                    const reviewTabIdinVIP = '5';
                    newPath = `${path}#${reviewTabIdinVIP}`;
                    trackGA4Event<GA4.GoogleReviewEvent>({
                        event: 'srp_review_click',
                        accountId: ad.accountId?.toString() || '',
                    });
                }
            } catch (error) {
                console.error('Error handling click:', error);
            }

            await handleListingClick({
                e,
                type,
                ad,
                path: newPath,
            });
        },
        [type, ad, path]
    );

    return (
        <Wrapper {...otherProps} {...qaAttribute('search-result')}>
            <A {...qaAttribute('search-result-anchor')} href={seoPath} onClick={handleClick}>
                {/* 顶部标签 */}
                {(featured || urgent) && (
                    <TopTagWrapper>
                        {featured && <ImageBadge isFeatured>Featured</ImageBadge>}
                        {urgent && (
                            <ImageBadge isUrgent isNewUiForCategory={isNewUiForCategory}>
                                {isNewUiForCategory ? 'Good Availability' : 'Urgent'}
                            </ImageBadge>
                        )}
                    </TopTagWrapper>
                )}
                {/* 收藏 */}
                {isShowFavourite && adSource !== 'Bark' && (
                    <FavouriteWrapper>
                        <Favourite onClick={toggleFavourite} isActive={favourited} />
                    </FavouriteWrapper>
                )}

                <AContentWrapper>
                    <ImageScoreWrapper>
                        {/* 头图 */}
                        {tileHasImages && (
                            <ImageWrapper>
                                <ListingTileThumbnail
                                    isLazy={isLazyLoadImage}
                                    image={image}
                                    defaultImg={NoImg}
                                />
                            </ImageWrapper>
                        )}
                        {!isMobile &&
                            average &&
                            total &&
                            (adSource === 'Bark' || inExperiment_GOOGLE_REVIEW_SRP) && (
                                <ScoreReviews
                                    score={average}
                                    reviewCount={total}
                                    type="srp"
                                    isMobile={isMobile}
                                />
                            )}
                    </ImageScoreWrapper>

                    <ContentWrapper>
                        <H2 isShowFavourite={isShowFavourite && !urgent && !featured}>{title}</H2>
                        <SecondWrapper>
                            <LocationWrapper>
                                <Icon type="location" />
                                <LocationName>{showLocation}</LocationName>
                            </LocationWrapper>
                            {categoryName && <CateName>{categoryName}</CateName>}
                        </SecondWrapper>

                        {isMobile &&
                            average &&
                            total &&
                            (adSource === 'Bark' || inExperiment_GOOGLE_REVIEW_SRP) && (
                                <ScoreReviews
                                    score={average}
                                    reviewCount={total}
                                    type="srp"
                                    isMobile={isMobile}
                                />
                            )}

                        {!isMobile && (
                            <TileInfoService
                                adId={adId}
                                isUserLoggedIn={userLoggedIn}
                                sellerSkills={sellerSkills}
                                imageUrl={imageUrl}
                                title={title}
                                // ctaData={{
                                //     isCurrentUserSeller: false,
                                //     replyEmail: "example@example.com",
                                //     replyPhone: "12345678",
                                //     phoneNumber: "123-456-7890",
                                //     pro: true,
                                //     containsCallTrackingFeature: true,
                                //     isFromMotorsFeed: false,
                                // }}
                                ctaData={srpContactDetail}
                                trackEventPayload={getClickListingDetails(ad)}
                                adSource={adSource}
                                thirdPartySellerId={thirdPartySellerId}
                                allSellerSkills={allSellerSkills}
                                updateRequestQuoteFormData={updateRequestQuoteFormData}
                                requestId={requestId}
                                categoryId={categoryId}
                            />
                        )}
                    </ContentWrapper>
                </AContentWrapper>

                {isMobile && (
                    <TileInfoService
                        adId={adId}
                        isUserLoggedIn={userLoggedIn}
                        sellerSkills={sellerSkills}
                        imageUrl={imageUrl}
                        title={title}
                        adSource={adSource}
                        thirdPartySellerId={thirdPartySellerId}
                        allSellerSkills={allSellerSkills}
                        ctaData={srpContactDetail}
                        trackEventPayload={getClickListingDetails(ad)}
                        updateRequestQuoteFormData={updateRequestQuoteFormData}
                        requestId={requestId}
                        categoryId={categoryId}
                    />
                )}
            </A>
        </Wrapper>
    );
};

export default ListingTile;
